// src/context/WebSocketContext.tsx
//
// Ported from the old project's WebSocketContext. Real functionality kept
// in full: token resync on login, STOMP connection via useStompClient,
// /user/queue/live-notify (+ the plain /queue/live-notify variant) for
// go-live/end-live signals, polling live/hosts as a backstop, and the
// sessionId-scoped lastEndedCreator tracking.
//
// Changed to fit this project:
//   - @/lib/hook -> @/services/hook, @/lib/store -> @/services/store
//     (this project's own convention, confirmed from PostComposer etc.)
//   - @/hooks/apiCalls's useGetData -> @/hooks/api/use-api (same file
//     useInfiniteGetData already comes from elsewhere in this project)
//   - showInlineToast (old project's toast call) -> this project's actual
//     convention, S.toast(msg, tone) via useAppStore. Per your standing
//     rule: this project's toast, never the old one.
//   - Dropped the entire commented-out earlier version that was sitting
//     above the real one in the file you pasted — kept only the final,
//     active implementation.
//   - Types come from live-types.ts (or wherever you merge them) instead
//     of @/lib/types.
//
// Needs mounting once near your app root, same as NotificationsProvider —
// useWebSocket() throws without it.

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useAppSelector } from "@/services/hook";
import type { RootState } from "@/services/store";
import { useAppStore } from "@/lib/core";
import { useGetData } from "@/hooks/api/use-api";
import { useStompClient } from "@/hooks/useStompClient";
import type { StompSubscription } from "@stomp/stompjs";
import {
  LiveCreator,
  LiveNotification,
  WebSocketContextType,
} from "@/utils/liveStreamingTypes";

interface WebSocketProviderProps {
  children: React.ReactNode;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined,
);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  const { userObject } = useAppSelector((state: RootState) => state.auth);
  const toast = useAppStore((s) => s.toast);
  const hydrateBlocked = useAppStore((s) => s.hydrateBlocked);

  // S.blocked is in-memory-only (no persist middleware) — without this, a
  // real block made via the backend would silently stop being enforced on
  // the very next page refresh, since the local store resets to {} every
  // load. Synced once per session here since this provider already mounts
  // once at the app root with userObject available.
  const { data: blockedData } = useGetData({
    url: "profile/block",
    queryKey: ["GetBlockedUsers"],
    enabled: !!userObject?.email,
  });

  useEffect(() => {
    const payload = (blockedData as { data?: unknown } | undefined)?.data;
    const list: Array<Record<string, unknown>> = Array.isArray(payload)
      ? (payload as Array<Record<string, unknown>>)
      : ((payload as { content?: Array<Record<string, unknown>> } | undefined)
          ?.content ?? []);

    const handles = list
      .map((entry) =>
        typeof entry === "string" ? entry : (entry?.username as string | undefined),
      )
      .filter((h): h is string => !!h);

    // TEMP DIAGNOSTIC — remove once confirmed. GET subscriptions/following
    // turned out to be a flat array of EMAILS, not usernames or objects, a
    // shape nothing here anticipated the first time either — this checks
    // whether GET profile/block has the same surprise. Every S.blocked
    // check in this app is keyed by USERNAME (S.blocked[creator.username]),
    // so if this list is actually emails, hydration "succeeds" with no
    // error but silently never filters anyone after a refresh.
    if (blockedData !== undefined) {
      console.log("[blocked-hydration] raw profile/block response:", blockedData, {
        extractedHandles: handles,
      });
    }

    if (handles.length) hydrateBlocked(handles);
  }, [blockedData, hydrateBlocked]);

  // Token used to be read once via `localStorage.getItem("token")` at
  // render time. If this provider mounted before login finished, `token`
  // stayed null forever for that instance and the socket never connected
  // even after the user logged in. It's now state, resynced whenever a
  // login completes (useSignIn already dispatches "auth-complete") or
  // another tab changes localStorage.
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem("token"),
  );

  useEffect(() => {
    const syncToken = () => setToken(localStorage.getItem("token"));
    window.addEventListener("auth-complete", syncToken);
    window.addEventListener("storage", syncToken);
    return () => {
      window.removeEventListener("auth-complete", syncToken);
      window.removeEventListener("storage", syncToken);
    };
  }, []);

  const [liveCreators, setLiveCreators] = useState<Map<string, LiveCreator>>(
    new Map(),
  );

  const liveNotifySubscriptionRef = useRef<StompSubscription | undefined>(
    undefined,
  );
  const plainLiveNotifySubscriptionRef = useRef<StompSubscription | undefined>(
    undefined,
  );
  const notifiedCreatorsRef = useRef<Set<string>>(new Set());
  // Guards the one-time pre-seed below — without it, every 30s live/hosts
  // poll (not just the first one after connecting) would mark whoever's
  // currently live as "already notified," which could just as easily
  // swallow a real go-live toast that lands between two polls.
  const initialHostsSyncedRef = useRef(false);
  // Backstop for the pre-seed above: that approach only works if the
  // live/hosts REST response resolves before the socket's replay burst
  // arrives, and nothing actually guarantees that ordering — they're two
  // independent requests racing each other. This is a hard grace window
  // instead: no toast fires within GRACE_MS of the live-notify
  // subscription being established, full stop, regardless of which
  // request wins. Set once per subscribe, not per message.
  const subscribedAtRef = useRef(0);
  const LIVE_NOTIFY_GRACE_MS = 4000;

  const { data: getLiveHosts, refetch: refetchLiveHosts } = useGetData({
    url: `live/hosts`,
    queryKey: ["GetLiveHosts"],
    enabled: !!userObject,
    refetchInterval: 30000,
  });

  // Tracks the most recent "creator ended live" signal so any component
  // watching a specific creator's stream can react to it. Confirmed by
  // backend: this comes through the SAME /user/queue/live-notify
  // destination as "creator went live" — not a separate /topic/.../end
  // broadcast. Only the event field differs: CREATOR_LIVE_STARTED vs
  // CREATOR_ENDED_LIVE.
  const [lastEndedCreator, setLastEndedCreator] = useState<{
    creatorId: string;
    sessionId: string;
    timestamp: number;
  } | null>(null);

  const handleLiveNotification = useCallback(
    (payload: LiveNotification) => {
      const rawPayload = payload as any;
      // Confirmed from a real payload capture: the backend sends `creatorID`
      // (capital ID), not `creatorId`.
      const creatorId = rawPayload?.creatorID || rawPayload?.creatorId;
      if (!creatorId) return;

      const event = rawPayload?.event;

      // TEMP DIAGNOSTIC — remove once the creator->viewer delivery gap is
      // root-caused. Prints exactly what session id this event carries, so
      // it can be compared against the session id GoLivePage actually
      // published/subscribed with (logged there too). If these two values
      // ever differ for the same live stream, that confirms the viewer is
      // resolving the WRONG session — explaining blank video + missing
      // creator chat/reactions all at once.
      console.log("[live-notify] raw payload:", rawPayload);

      if (event === "CREATOR_ENDED_LIVE") {
        setLiveCreators((prev) => {
          if (!prev.has(creatorId)) return prev;
          const updated = new Map(prev);
          updated.delete(creatorId);
          return updated;
        });
        // Cleared so a future CREATOR_LIVE_STARTED for this creator shows
        // the toast again instead of being treated as already-notified.
        notifiedCreatorsRef.current.delete(creatorId);
        // Storing sessionId alongside creatorId: without it, this flag is
        // only scoped to "this creator ended a stream at some point," which
        // never expires. A viewer joining a BRAND NEW stream from the same
        // creator later would immediately (and wrongly) match on creatorId
        // alone. Consumers should compare against the current session id,
        // not just the creator.
        setLastEndedCreator({
          creatorId,
          sessionId: rawPayload.sessionId || rawPayload.session || "",
          timestamp: Date.now(),
        });
        return;
      }

      // Anything else (including the expected CREATOR_LIVE_STARTED, and
      // defensively any unrecognized event) is treated as "went live".
      const streamStartTime = rawPayload.timestamp || Date.now();

      setLiveCreators((prev) => {
        const withinGrace =
          subscribedAtRef.current > 0 &&
          Date.now() - subscribedAtRef.current < LIVE_NOTIFY_GRACE_MS;
        if (!notifiedCreatorsRef.current.has(creatorId)) {
          if (!withinGrace) toast(`🔴 ${creatorId} is now LIVE!`, "ok");
          notifiedCreatorsRef.current.add(creatorId);
        }

        // Always overwrite, even if this creatorId is already in the map —
        // a stale entry (from a previous stream, or a race with the
        // live/hosts poll) must not block this event's sessionId from
        // taking over. Silently keeping a stale sessionId here meant a
        // viewer could end up joining a dead Agora channel and subscribed
        // to the wrong /topic/live/{sessionId}/* STOMP topics entirely —
        // no video, no chat, with no visible error anywhere.
        const updated = new Map(prev);
        updated.set(creatorId, {
          creatorId,
          sessionId: rawPayload.session || rawPayload.sessionId || "",
          streamStartTime: Number(streamStartTime),
        });
        return updated;
      });
    },
    [toast],
  );

  const handleConnect = useCallback(() => {
    refetchLiveHosts();
  }, [refetchLiveHosts]);

  const { client, isConnected, subscribe, sendMessage } = useStompClient(
    token,
    { onConnect: handleConnect },
  );

  useEffect(() => {
    if (isConnected && subscribe) {
      if (liveNotifySubscriptionRef.current) {
        liveNotifySubscriptionRef.current.unsubscribe();
      }

      subscribedAtRef.current = Date.now();

      const topic = "/user/queue/live-notify";
      const sub = subscribe(topic, (message) => {
        try {
          const payload = JSON.parse(message.body);
          handleLiveNotification(payload);
        } catch (e) {
          console.error("Error parsing live notification:", e);
        }
      });

      liveNotifySubscriptionRef.current = sub;

      // The mobile app subscribes to BOTH `/user/queue/live-notify` (above)
      // AND a separate, plain `/queue/live-notify` — no `/user/` prefix.
      // `/user/queue/...` is special-cased by Spring (rewritten server-side
      // per session); a plain `/queue/...` behaves like any other broadcast
      // destination. Given "stream ended" rides the same notify mechanism
      // instead of the documented /topic/live/{sessionId}/end, there's a
      // real chance some events only ever arrive on this plain variant.
      // Logging raw for now rather than guessing a shape to parse.
      const plainTopic = "/queue/live-notify";
      const plainSub = subscribe(plainTopic, (message) => {
        console.log(
          "[plain live-notify, no /user/ prefix] raw message received:",
          message.body,
        );
      });

      plainLiveNotifySubscriptionRef.current = plainSub;

      return () => {
        if (liveNotifySubscriptionRef.current) {
          liveNotifySubscriptionRef.current.unsubscribe();
          liveNotifySubscriptionRef.current = undefined;
        }
        if (plainLiveNotifySubscriptionRef.current) {
          plainLiveNotifySubscriptionRef.current.unsubscribe();
          plainLiveNotifySubscriptionRef.current = undefined;
        }
      };
    }
  }, [isConnected, subscribe, handleLiveNotification]);

  // Per backend: every connected user (not just creators mid-stream) should
  // heartbeat their active status every 50-60s, same mechanism as the
  // existing /app/live/streaming heartbeat in studio/live.tsx (periodic
  // sendMessage on an interval, no immediate send on connect — mirrors that
  // one exactly). Lives here rather than per-page since "being connected to
  // the app at all" is the actual signal, not any specific page.
  useEffect(() => {
    if (!isConnected) return;
    const interval = setInterval(() => {
      sendMessage("/app/active/heartbeat", {});
    }, 55000);
    return () => clearInterval(interval);
  }, [isConnected, sendMessage]);

  useEffect(() => {
    const liveHostsResponse = getLiveHosts as any;
    if (!liveHostsResponse?.data) return;

    const liveHostsData = Array.isArray(liveHostsResponse.data)
      ? liveHostsResponse.data
      : liveHostsResponse.data.content || [];

    // The live-notify socket queue (below) replays a "you're now live"
    // message for every creator who's ALREADY live the moment a client
    // subscribes, not just future go-lives — so logging in while N
    // creators happen to be live fired N toasts, every time, for streams
    // that had already been running. This first live/hosts response is the
    // same "who's live right now" snapshot, just arriving over REST instead
    // of the socket; marking everyone in it as already-notified means that
    // replay burst gets silently deduped, while a creator who goes live
    // afterward — not in this snapshot — still toasts normally.
    if (!initialHostsSyncedRef.current) {
      initialHostsSyncedRef.current = true;
      liveHostsData.forEach((host: any) => {
        if (host?.creatorID) notifiedCreatorsRef.current.add(host.creatorID);
      });
    }

    setLiveCreators((prev) => {
      const updated = new Map(prev);
      const activeCreatorIds = new Set<string>();

      liveHostsData.forEach((host: any) => {
        const creatorId = host?.creatorID;
        if (creatorId) {
          activeCreatorIds.add(creatorId);
          updated.set(creatorId, {
            creatorId,
            sessionId: host?.session,
            streamStartTime: host?.liveAt ? Number(host.liveAt) : undefined,
          });
        }
      });

      for (const id of updated.keys()) {
        if (!activeCreatorIds.has(id)) {
          updated.delete(id);
          notifiedCreatorsRef.current.delete(id);
        }
      }

      return updated;
    });
  }, [getLiveHosts]);

  const removeCreatorFromLive = useCallback((creatorId: string) => {
    setLiveCreators((prev) => {
      if (!prev.has(creatorId)) return prev;
      const updated = new Map(prev);
      updated.delete(creatorId);
      return updated;
    });
    notifiedCreatorsRef.current.delete(creatorId);
  }, []);

  const isCreatorLive = useCallback(
    (creatorId: string) => liveCreators.has(creatorId),
    [liveCreators],
  );

  const getLiveSession = useCallback(
    (creatorId: string) => liveCreators.get(creatorId),
    [liveCreators],
  );

  const value: WebSocketContextType = {
    isConnected,
    client,
    liveCreators,
    sendMessage,
    isCreatorLive,
    getLiveSession,
    refetchLiveHosts,
    removeCreatorFromLive,
    lastEndedCreator,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
